import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { LinkIcon } from '@heroicons/react/24/outline';
import {
  crmApi, type CrmLeadLink, type CrmLeadProject, type CrmLeadResponse, type LeadFieldDef, type LeadLinkStatus,
} from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';
import { cardClass, inputClass, labelClass } from './styles';

const STATUS_COLOR: Record<LeadLinkStatus, string> = {
  activo: 'bg-green-100 text-green-700',
  caducado: 'bg-gray-100 text-gray-600',
  anulado: 'bg-red-50 text-red-700',
};
const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '—');
const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

/** Enlace personal del lead (ALTA LEAD 1.3) y sus respuestas originales. */
export default function LeadLinkSection({ contactId, hasEmail }: { contactId: string; hasEmail: boolean }) {
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.CRM_MANAGE));
  const [links, setLinks] = useState<CrmLeadLink[]>([]);
  const [responses, setResponses] = useState<CrmLeadResponse[]>([]);
  const [fields, setFields] = useState<LeadFieldDef[]>([]);
  const [creating, setCreating] = useState(false);
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const [showResponses, setShowResponses] = useState(false);

  const load = async () => {
    try {
      const [l, r, f] = await Promise.all([
        crmApi.listLeadLinks(contactId), crmApi.listLeadResponses(contactId), crmApi.getLeadFields(),
      ]);
      setLinks(l.data.links || []);
      setResponses(r.data.responses || []);
      setFields(f.data.fields || []);
    } catch { /* sin permiso: silencioso */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [contactId]);

  const label = useMemo(() => new Map(fields.map((f) => [f.key, f.label])), [fields]);

  const revoke = async (l: CrmLeadLink) => {
    if (!confirm('¿Anular este enlace? El lead ya no podrá usarlo.')) return;
    try { await crmApi.revokeLeadLink(l._id); toast.success('Enlace anulado'); void load(); }
    catch (e) { toast.error(errMsg(e, 'No se pudo anular')); }
  };

  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><LinkIcon className="w-5 h-5 text-vida-600" aria-hidden="true" /> Enlace para el lead</h3>
        {canManage && (
          <button onClick={() => { setNewUrl(null); setCreating(true); }} className="text-sm font-medium text-vida-700 hover:bg-vida-50 px-2 py-1 rounded">
            Crear enlace
          </button>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-1">El lead rellena solo los campos que elijas. Caduca a los 5 días.</p>

      {newUrl && (
        <div className="mt-3 rounded-lg bg-vida-50 border border-vida-200 p-3 text-sm">
          <p className="text-vida-800 font-medium mb-1">Enlace creado. Cópialo ahora: no se vuelve a mostrar.</p>
          <div className="flex gap-2">
            <input readOnly aria-label="Enlace del lead" className={`${inputClass} font-mono text-xs`} value={newUrl} onFocus={(e) => e.target.select()} />
            <button onClick={() => { void navigator.clipboard.writeText(newUrl); toast.success('Copiado'); }}
              className="px-3 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700">Copiar</button>
          </div>
        </div>
      )}

      {links.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {links.map((l) => (
            <li key={l._id} className="flex items-center gap-2 text-sm rounded px-2 py-1.5 hover:bg-gray-50">
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLOR[l.status]}`}>{l.status}</span>
              <span className="flex-1 min-w-0 truncate text-gray-700" title={l.fields.map((k) => label.get(k) || k).join(', ')}>
                {l.fields.length} campo{l.fields.length === 1 ? '' : 's'} · caduca {fmt(l.expiresAt)}
                {l.sentTo ? ` · enviado a ${l.sentTo}` : ' · no enviado'}
                {l.submissions ? ` · ${l.submissions} respuesta${l.submissions === 1 ? '' : 's'}` : ''}
              </span>
              {canManage && l.status === 'activo' && (
                <button onClick={() => void revoke(l)} className="text-xs text-gray-500 hover:text-red-600">Anular</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {responses.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowResponses(!showResponses)} aria-expanded={showResponses} className="text-sm font-medium text-gray-700 hover:text-vida-700">
            Respuestas originales del lead ({responses.length}) {showResponses ? '▲' : '▼'}
          </button>
          {showResponses && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-gray-400">Lo que escribió el lead, tal cual. No cambia aunque se edite la ficha.</p>
              {responses.map((r) => (
                <div key={r._id} className="rounded-lg border border-gray-200 p-3 text-sm">
                  <p className="text-xs text-gray-400 mb-1">{new Date(r.created_at).toLocaleString('es-ES')}</p>
                  <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-1">
                    {Object.entries(r.values).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="text-gray-500">{label.get(k) || k}</dt>
                        <dd className="sm:col-span-2 text-gray-800 whitespace-pre-wrap break-words">{typeof v === 'boolean' ? (v ? 'Sí' : 'No') : v || '—'}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {creating && (
        <CreateLinkModal contactId={contactId} fields={fields} hasEmail={hasEmail}
          onClose={() => setCreating(false)}
          onCreated={(url, sent) => {
            setCreating(false);
            setNewUrl(url);
            toast.success(sent ? 'Enlace enviado al lead' : 'Enlace creado');
            void load();
          }} />
      )}
    </div>
  );
}

function CreateLinkModal({ contactId, fields, hasEmail, onClose, onCreated }: {
  contactId: string; fields: LeadFieldDef[]; hasEmail: boolean; onClose: () => void; onCreated: (url: string, sent: boolean) => void;
}) {
  const [projects, setProjects] = useState<CrmLeadProject[]>([]);
  const [projectId, setProjectId] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [send, setSend] = useState(hasEmail);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    crmApi.listProjects(contactId).then((r) => setProjects(r.data.projects || [])).catch(() => {});
  }, [contactId]);

  const visible = fields.filter((f) => projectId || !f.key.startsWith('project.'));
  const groups = useMemo(() => {
    const map = new Map<string, LeadFieldDef[]>();
    for (const f of visible) map.set(f.group, [...(map.get(f.group) || []), f]);
    return [...map.entries()];
  }, [visible]);

  const toggle = (key: string) => setSelected(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  const toggleGroup = (keys: string[]) => {
    const all = keys.every((k) => selected.includes(k));
    setSelected(all ? selected.filter((k) => !keys.includes(k)) : [...new Set([...selected, ...keys])]);
  };

  const submit = async () => {
    const chosen = selected.filter((k) => visible.some((f) => f.key === k));
    if (chosen.length === 0) { toast.error('Elige al menos un campo'); return; }
    setBusy(true);
    try {
      const r = await crmApi.createLeadLink(contactId, { fields: chosen, projectId: projectId || null, send });
      if (send && !r.data.sent) toast.error('El enlace se creó pero no se pudo enviar el correo: cópialo y envíalo a mano');
      onCreated(r.data.url, r.data.sent);
    } catch (e) { toast.error(errMsg(e, 'No se pudo crear el enlace')); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div className={`${cardClass} w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto`} role="dialog" aria-modal="true" aria-label="Crear enlace para el lead">
        <h2 className="text-lg font-semibold text-gray-900">Crear enlace para el lead</h2>
        <p className="text-xs text-gray-500 mt-1">Solo se pueden enviar datos de la parte que ve el lead. La valoración interna y el contacto comercial nunca salen.</p>

        <div className="mt-4">
          <label className={labelClass} htmlFor="lead-link-project">Proyecto (para enviar sus campos)</label>
          <select id="lead-link-project" className={inputClass} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Sin proyecto</option>
            {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        </div>

        <div className="mt-4 space-y-3">
          {groups.map(([group, list]) => (
            <fieldset key={group} className="border border-gray-200 rounded-lg p-3">
              <legend className="px-1 text-sm font-semibold text-gray-900">{group}</legend>
              <button type="button" onClick={() => toggleGroup(list.map((f) => f.key))} className="text-xs text-vida-700 hover:underline mb-2">
                {list.every((f) => selected.includes(f.key)) ? 'Quitar todos' : 'Elegir todos'}
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {list.map((f) => (
                  <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={selected.includes(f.key)} onChange={() => toggle(f.key)} /> {f.label}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 mt-4">
          <input type="checkbox" disabled={!hasEmail} checked={send && hasEmail} onChange={(e) => setSend(e.target.checked)} />
          Enviarlo por correo al lead
          {!hasEmail && <span className="text-xs text-gray-400">(la ficha no tiene email: tendrás que copiarlo)</span>}
        </label>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">
            {busy ? 'Creando…' : send && hasEmail ? 'Crear y enviar' : 'Crear enlace'}
          </button>
        </div>
      </div>
    </div>
  );
}
